import React from 'react'
import AssinaturaRota from '../AssinaturaRota'
import { useEntrada, atraso } from '../../lib/entrada'
import { PALESTRANTES, ANCORA_INSCRICAO } from '../../dados/evento'

/** Iniciais para o monograma que substitui a foto que ainda não chegou. */
function iniciais(nome) {
  const partes = nome.trim().split(/\s+/)
  const primeira = partes[0]?.[0] ?? ''
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : ''
  return (primeira + ultima).toUpperCase()
}

/**
 * Ícone do TEMA que a pessoa leva ao palco, não dela: a foto já diz quem é, e o
 * que o cartão não dizia num relance era sobre o que a pessoa vem falar. Um
 * nome desconhecido ao lado de uma balança e de "Reforma Tributária" se
 * explica sozinho.
 *
 * Inline em vez de pacote: são quatro traçados, e a lib inteira pesaria
 * centenas de KB para servir quatro cards.
 */
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

/**
 * 3ª dobra — quem sobe ao palco.
 *
 * REESCRITA EM 11/08, e a reescrita é sobretudo uma REMOÇÃO. A versão anterior
 * tinha três mecanismos animados sobrepostos: uma trilha tracejada em GSAP
 * ligando os nomes de um card ao outro, um "cursor magnético" que inclinava o
 * card sob o ponteiro, e um contador 01/04 que acompanhava a rolagem. Cerca de
 * 250 linhas de JavaScript para decorar quatro fotos.
 *
 * Foram embora porque cobravam o preço no lugar errado. A trilha nascia do FIM
 * do nome, o que obrigava o nome a dividir a linha com ela; com nomes de dois
 * elementos ("Marcos Alexandre Claudino") o nome era espremido e cortado — o
 * defeito que o cliente apontou duas vezes. O que o briefing pede desta dobra
 * é o oposto: "as pessoas são protagonistas, fotos grandes, nome muito
 * legível". Nada disso se obtém somando efeito; obtém-se tirando tudo o que
 * disputa espaço com a foto e com o nome.
 *
 * A HIERARQUIA agora é explícita e tem três degraus, na ordem em que a decisão
 * acontece: (1) a FOTO, em retrato grande de 4:5; (2) o NOME, em display 800;
 * (3) o TEMA da palestra, com o ícone. O cargo e a empresa ficam em quarto,
 * como legenda — é a informação que confirma a autoridade depois que o nome já
 * chamou, não antes.
 *
 * A descrição da palestra saiu do card. Ela repetia o tema com outras palavras
 * ("Panorama do Mercado Imobiliário Regional" / "Dados, tendências e
 * estratégias para crescer com mais eficiência") e era exatamente o "excesso
 * de texto pequeno" que o briefing manda evitar. O campo continua em
 * `dados/evento.js`, intacto, e volta a renderizar no dia em que a programação
 * completa exigir uma sinopse de verdade.
 *
 * SEM CARROSSEL, em nenhuma largura: são quatro pessoas, e quatro cabem em
 * 2×2 no celular sem que ninguém precise arrastar para descobrir que existe um
 * quarto palestrante. O briefing é explícito — "não crie carousel só por
 * criar".
 */
export default function SecaoPalestrantes({
  chamada = 'Palestrantes confirmados',
  subtitulo = 'Em breve, programação completa',
  palestrantes = PALESTRANTES,
  textoCta = 'Garantir minha presença',
  /* O MASCOTE VOLTOU em 12/08 (PDF do cliente): "tbm colocar ao lado o
     [Nauta com a bandeira branca]". A pose do PDF é a `nauta-rota-1` — é a
     única da família com bandeira BRANCA, e é literalmente a arte que ele
     colou no documento.

     O motivo pelo qual ele tinha saído continua valendo e está resolvido por
     COMPOSIÇÃO, não por remoção: ele flutuava solto ao lado do título, sem
     chão. Agora entra na faixa do cabeçalho, sobre um disco de luz (o mesmo
     recurso do Apre.Lab), à direita do bloco de título — e some abaixo de
     64rem, onde a coluna deixa de existir e ele voltaria a competir com os
     quatro retratos. */
  mascote = '/img/nauta-rota-1-web.png',
}) {
  const ref = useEntrada()

  /* O título é a peça gráfica da dobra na arte aprovada: caixa alta, duas
     linhas, a segunda em azul. Mesma partição do H1 do hero — parte na última
     palavra, e com uma palavra só sai numa linha, sem quebrar nada. */
  const palavras = chamada.trim().split(' ')
  const ultima = palavras.length > 1 ? palavras.pop() : ''
  const resto = palavras.join(' ')

  return (
    <section className="secao secao-palestrantes noite" id="palestrantes" ref={ref}>
      <div className="container-site palestrantes-topo">
        {/* Cabeçalho centralizado, com o nome da dobra em escala de cartaz —
            era um cabeçalho de duas colunas com o apoio à direita. */}
        <div className="cabecalho-secao cabecalho-centro entra">
          <span className="eyebrow">Quem sobe ao palco</span>
          <h2 className="palestrantes-titulo display-cartaz">
            <span className="apenas-leitor">{chamada}</span>
            <span aria-hidden="true">
              {resto}
              {ultima && (
                <>
                  <br />
                  <span className="realce">{ultima}</span>
                </>
              )}
            </span>
          </h2>
          <p className="palestrantes-nota">
            <span className="estrela-fixa" aria-hidden="true" />
            {subtitulo}
            <span className="estrela-fixa" aria-hidden="true" />
          </p>
        </div>

        {/* O Nauta ao lado do título, sobre um disco de luz. Decorativo. */}
        {mascote && (
          <figure className="palestrantes-nauta entra" aria-hidden="true">
            <span className="palestrantes-nauta-disco" />
            <img src={mascote} alt="" width="420" height="420" loading="lazy" />
          </figure>
        )}
      </div>

      <div className="container-site">
        <ul className="grade-palestrantes lista-limpa">
          {palestrantes.map((p, i) => {
            return (
              <li key={p.nome} className="entra" style={atraso(i, 80)}>
                <article className="cartao-palestrante cartao-arte">
                  <div className="palestrante-retrato">
                    {p.foto ? (
                      <img src={p.foto} alt={p.nome} loading="lazy" width="640" height="800" />
                    ) : (
                      <span className="palestrante-monograma" aria-hidden="true">
                        {iniciais(p.nome)}
                      </span>
                    )}
                    {/* O SELO DE TEMA SOBRE O RETRATO SAIU em 12/08, a pedido
                        do cliente, e ele tem razão: um ícone de gráfico ou de
                        balança carimbado no canto da FOTO DE UMA PESSOA não
                        diz nada sobre ela. O tema da palestra já aparece por
                        extenso logo abaixo do nome, que é onde se lê. */}
                  </div>

                  <div className="palestrante-corpo">
                    {/* O nome ocupa a linha inteira e nada divide espaço com
                        ele. `<h3>` porque é o título do cartão — o tema da
                        palestra é subordinado à pessoa nesta composição. */}
                    <h3 className="palestrante-nome">{p.nome}</h3>

                    <p className="palestrante-palestra">{p.palestra}</p>

                    {/* A sinopse voltou ao cartão: na arte ela é a linha de
                        apoio entre o tema e o cargo, e é o que diferencia
                        quatro cartões que de outra forma teriam só nome e
                        título. Campo opcional — sem ele o cartão fecha no
                        rodapé do cargo. */}
                    {p.descricao && <p className="palestrante-descricao">{p.descricao}</p>}

                    <p className="palestrante-cargo">
                      <span className="palestrante-cargo-icone" aria-hidden="true">
                        <svg {...base} width="14" height="14">
                          <path d="M12 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                          <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                        </svg>
                      </span>
                      <span>{p.cargo}</span>
                    </p>
                  </div>
                </article>
              </li>
            )
          })}
        </ul>

        {/* A ASSINATURA DA ROTA VOLTOU, UMA VEZ SÓ (12/08, PDF do cliente).
            Ela tinha saído no mesmo dia por aparecer TRÊS vezes na página
            (fim do palco, fim dos palestrantes, rodapé) com as mesmas quatro
            frases. O PDF a mostra exatamente aqui e pede "aumentar o ícone e
            texto em 200% sempre q eles aparecerem" — ou seja, o problema dele
            nunca foi a faixa existir, era ela ser pequena demais para ser
            lida.

            ⚠️ UMA aparição. Se alguém recolocar no palco e no rodapé, volta o
            defeito que motivou a remoção. A escala de 200% está em
            revisao.css §5. */}
        <AssinaturaRota variante="destaque" className="palestrantes-assinatura entra" />

        <div className="secao-acao entra">
          {/* BOTÃO CHEIO (13/08, pedido do cliente). Mesma correção do
              "para quem é" — ver o comentário em SecaoPublico.jsx. */}
          <a href={ANCORA_INSCRICAO} className="btn btn-rota">
            {textoCta}
            <span className="btn-seta" aria-hidden="true">→</span>
          </a>
        </div>

      </div>
    </section>
  )
}
